import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import LinearProgress from "@mui/material/LinearProgress";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { AssignTag } from "./components/AssignTag";
import { EventForm } from "./components/EventForm";
import { ScanStep } from "./components/ScanStep";
import { usePhonePosition } from "./hooks/usePhonePosition";
import { TagRead } from "./utils/readTag";
import { useGeotabClient } from "@/components/GeotabClientContext";
import AddInIds from "@/constants/AddInIds";
import { findBy, setItem } from "@/hooks/assetScan/assetScanStore";
import { useGeotabCall, useScanList } from "@/hooks/assetScan/useScanList";
import { useRecordEvent } from "@/hooks/assetScan/useScanMutations";
import { ScanAsset, ScanCustomer, ScanEvent, ScanEventType, Stored } from "@/models/assetScan/AssetScanTypes";
import { EventDraft, eventTypesOrBuiltIn } from "@/models/assetScan/validateEvent";

type Step =
  | { kind: "scan" }
  | { kind: "looking"; read: TagRead }
  | { kind: "assign"; read: TagRead }
  | { kind: "event"; read: TagRead; asset: Stored<ScanAsset> };

/** Above this the phone's position is shown as doubtful; saving still works. */
const POOR_ACCURACY_M = 100;

const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** The scan screen for the phone: tag → asset → what happened → saved. */
export default function AssetScanPage() {
  const { t } = useTranslation();
  const { me } = useGeotabClient();
  const call = useGeotabCall();
  const [step, setStep] = useState<Step>({ kind: "scan" });
  const [lastRead, setLastRead] = useState<TagRead>();
  const [showDiagnosis, setShowDiagnosis] = useState(false);
  const [message, setMessage] = useState<{ severity: "success" | "warning" | "error"; text: string }>();
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const position = usePhonePosition();
  const recordEvent = useRecordEvent();
  const { data: typeRows = [] } = useScanList<ScanEventType>(AddInIds.assetScanEventTypes);
  const { data: customers = [] } = useScanList<ScanCustomer>(AddInIds.assetScanCustomers);
  const types = eventTypesOrBuiltIn(typeRows.map(row => row.details));

  const handleRead = useCallback(
    async (read: TagRead) => {
      setLastRead(read);
      setMessage(undefined);
      setSaveError(undefined);
      setStep({ kind: "looking", read });
      try {
        const byTag = read.tag ? await findBy<ScanAsset>(call, AddInIds.assetScanAssets, "tag", read.tag) : [];
        const bySerial =
          byTag.length === 0 && read.serial
            ? await findBy<ScanAsset>(call, AddInIds.assetScanAssets, "nfcSerial", read.serial)
            : [];
        const asset = byTag[0] ?? bySerial[0];
        if (asset) setStep({ kind: "event", read, asset });
        else if (read.via === "NFC") setStep({ kind: "assign", read });
        else {
          setMessage({ severity: "warning", text: t("assetScan.scan.notFound", { tag: read.tag ?? read.raw?.[0] ?? "" }) });
          setStep({ kind: "scan" });
        }
      } catch (e) {
        setMessage({ severity: "error", text: errorText(e) });
        setStep({ kind: "scan" });
      }
    },
    [call, t],
  );

  const handleAssign = async (asset: Stored<ScanAsset>) => {
    if (step.kind !== "assign") return;
    setBusy(true);
    setSaveError(undefined);
    try {
      if (window.NDEFReader) {
        await new window.NDEFReader().write({ records: [{ recordType: "text", data: asset.details.tag }] }, { overwrite: true });
      }
      const next: ScanAsset = { ...asset.details, nfcSerial: step.read.serial ?? asset.details.nfcSerial };
      await setItem(call, AddInIds.assetScanAssets, asset.id, next, asset.details, asset.details.groupIds);
      setStep({ kind: "event", read: { ...step.read, tag: asset.details.tag }, asset: { id: asset.id, details: next } });
    } catch (e) {
      setSaveError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async (type: ScanEventType, draft: EventDraft) => {
    if (step.kind !== "event") return;
    setSaveError(undefined);
    setBusy(true);
    try {
      let place: { lat?: number; lon?: number; accuracy?: number } = {};
      let poor = false;
      try {
        place = await position.request();
        poor = (place.accuracy ?? 0) > POOR_ACCURACY_M;
      } catch {
        if (type.changesLocation) throw new Error(t("assetScan.scan.positionNeeded"));
      }
      const event: ScanEvent = {
        asset: step.asset.details.tag,
        eventTypeKey: type.key,
        eventTypeName: type.name,
        time: new Date().toISOString(),
        ...place,
        user: me?.name ?? "",
        via: step.read.via,
        customerId: draft.customerId,
        note: draft.note?.trim() || undefined,
      };
      await recordEvent.mutateAsync({ event, asset: step.asset, types });
      setMessage({
        severity: poor ? "warning" : "success",
        text: poor
          ? t("assetScan.scan.savedPoorPosition", { name: step.asset.details.name, meters: place.accuracy })
          : t("assetScan.scan.saved", { name: step.asset.details.name, type: type.name }),
      });
      setStep({ kind: "scan" });
    } catch (e) {
      setSaveError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const serialMismatch =
    step.kind === "event" &&
    !!step.read.serial &&
    !!step.asset.details.nfcSerial &&
    step.read.serial !== step.asset.details.nfcSerial;

  return (
    <Box sx={{ maxWidth: 480, mx: "auto", p: 2 }}>
      {message && (
        <Alert severity={message.severity} onClose={() => setMessage(undefined)} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}
      {step.kind === "scan" && <ScanStep onRead={read => void handleRead(read)} />}
      {step.kind === "looking" && <LinearProgress aria-label={t("assetScan.scan.looking")} />}
      {step.kind === "assign" && (
        <AssignTag
          serial={step.read.serial}
          canWrite={!!window.NDEFReader}
          busy={busy}
          error={saveError}
          onAssign={asset => void handleAssign(asset)}
          onCancel={() => setStep({ kind: "scan" })}
        />
      )}
      {step.kind === "event" && (
        <EventForm
          key={step.asset.id}
          asset={step.asset}
          types={types}
          customers={customers}
          serialMismatch={serialMismatch}
          saving={busy}
          error={saveError}
          onSave={(type, draft) => void handleSave(type, draft)}
          onCancel={() => setStep({ kind: "scan" })}
        />
      )}

      <Box sx={{ mt: 4 }}>
        <Link component="button" variant="caption" onClick={() => setShowDiagnosis(open => !open)}>
          {t("assetScan.scan.diagnosis")}
        </Link>
        <Collapse in={showDiagnosis}>
          <Typography component="pre" variant="caption" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-all", mt: 1 }}>
            {lastRead
              ? [
                  `via: ${lastRead.via}`,
                  `serial: ${lastRead.serial ?? "–"}`,
                  `tag: ${lastRead.tag ?? "–"}`,
                  ...(lastRead.raw ?? []).map(line => `record ${line}`),
                ].join("\n")
              : t("assetScan.scan.diagnosisEmpty")}
          </Typography>
        </Collapse>
      </Box>
    </Box>
  );
}
